import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BIRD_EVENT_TYPE, type BirdEventRecord, type BirdEventType } from '@henlytics/types'
import { Count, Money, Percent } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { DateCell } from '../components/entry/DateCell'
import { EntryGrid } from '../components/entry/EntryGrid'
import { NumericCell } from '../components/entry/NumericCell'
import { KpiRow } from '../components/kpi/KpiRow'
import { KpiTile } from '../components/kpi/KpiTile'
import { useBatch } from '../hooks/useBatches'
import { useBirdEvents, useCreateBirdEvent, useVoidBirdEvent } from '../hooks/useBirdEvents'
import { useBatchCost } from '../hooks/useCosting'
import { today } from '../hooks/useReports'
import { clientUuid } from '../lib/uuid'

const SALVAGE_TYPES: readonly BirdEventType[] = ['cull', 'sale']

export function BatchDetail() {
  const { id = '' } = useParams()
  const asOf = today()

  const batch = useBatch(id)
  const cost = useBatchCost(id, asOf)
  const events = useBirdEvents(id)
  const createEvent = useCreateBirdEvent(id)
  const voidEvent = useVoidBirdEvent(id)

  const [eventDate, setEventDate] = useState(asOf)
  const [eventType, setEventType] = useState<BirdEventType>('mortality')
  const [qty, setQty] = useState('')
  const [salvage, setSalvage] = useState('')
  const [reason, setReason] = useState('')
  const [entryId, setEntryId] = useState(() => clientUuid())

  const columns: ReadonlyArray<Column<BirdEventRecord>> = [
    { key: 'date', header: 'Date', render: (e) => e.eventDate },
    { key: 'type', header: 'Event', render: (e) => e.eventType },
    { key: 'qty', header: 'Birds', numeric: true, render: (e) => <Count value={e.qtyDelta} /> },
    { key: 'salvage', header: 'Salvage', numeric: true, render: (e) => <Money value={e.salvageAmount} /> },
    { key: 'reason', header: 'Reason', render: (e) => e.reason ?? '—' },
    {
      key: 'actions',
      header: '',
      render: (e) =>
        e.voidedAt ? (
          <span className="muted">voided</span>
        ) : (
          <button
            type="button"
            className="link-button"
            onClick={() => voidEvent.mutate({ eventId: e.id })}
            disabled={voidEvent.isPending}
          >
            Void
          </button>
        ),
    },
  ]

  if (batch.isPending || cost.isPending) return <p className="muted">Loading…</p>
  if (batch.isError) return <p role="alert" className="error">{batch.error.message}</p>
  if (cost.isError) return <p role="alert" className="error">{cost.error.message}</p>

  const report = cost.data

  return (
    <div className="page">
      <header className="page-head">
        <h2>
          Batch {batch.data.code} <span className="muted">{report.isLaying ? 'laying' : 'rearing'}</span>
        </h2>
        <p className="muted">
          Arrived {batch.data.arrivalDate} · layer start {report.layerStartDate} · week{' '}
          {Math.floor(report.ageDays / 7)} · <Link to={`/entry?batch=${id}`}>daily entry</Link>
        </p>
      </header>

      <KpiRow>
        <KpiTile
          label="Living birds"
          value={<Count value={report.production.population} />}
          secondary={{ label: 'Mortality', value: <Percent value={report.production.mortalityPct} /> }}
        />
        <KpiTile
          label="Accumulated cost"
          value={<Money value={report.cost.accumulatedCost.inclOverhead} />}
          secondary={{
            label: 'Excl. overhead',
            value: <Money value={report.cost.accumulatedCost.exclOverhead} />,
          }}
          hint="Overhead allocation is defensible but contested — both figures are always shown (§3.6)."
        />
        <KpiTile
          label="Cost per bird"
          value={<Money value={report.cost.costPerBird?.inclOverhead ?? null} />}
          secondary={{
            label: 'Excl. overhead',
            value: <Money value={report.cost.costPerBird?.exclOverhead ?? null} />,
          }}
        />
        <KpiTile
          label="Cash cost / egg"
          value={<Money value={report.costPerEgg.cash?.inclOverhead ?? null} precision={3} />}
          secondary={{
            label: 'Loaded / egg',
            value: <Money value={report.costPerEgg.loaded?.inclOverhead ?? null} precision={3} />,
          }}
          hint="Cash answers “is this flock cash-positive today?”. Loaded adds rearing amortised over expected lifetime lay."
        />
      </KpiRow>

      <section>
        <h3>Cost breakdown</h3>
        <table className="data-table breakdown">
          <tbody>
            <tr><th scope="row">Chicks</th><td className="numeric"><Money value={report.cost.chickCost} /></td></tr>
            <tr><th scope="row">Feed</th><td className="numeric"><Money value={report.cost.feedCost} /></td></tr>
            <tr><th scope="row">Vaccination</th><td className="numeric"><Money value={report.cost.vaccinationCost} /></td></tr>
            <tr><th scope="row">Medical</th><td className="numeric"><Money value={report.cost.medicalCost} /></td></tr>
            <tr><th scope="row">Labor</th><td className="numeric"><Money value={report.cost.laborCost} /></td></tr>
            <tr><th scope="row">Overhead</th><td className="numeric"><Money value={report.cost.overheadCost} /></td></tr>
            <tr><th scope="row">Other</th><td className="numeric"><Money value={report.cost.otherCost} /></td></tr>
            <tr><th scope="row">Salvage credit</th><td className="numeric">−<Money value={report.cost.salvageCredit} /></td></tr>
            <tr className="total"><th scope="row">Accumulated</th><td className="numeric"><Money value={report.cost.accumulatedCost.inclOverhead} /></td></tr>
          </tbody>
        </table>
        <p className="muted">
          Rearing total <Money value={report.layer.rearingCostTotal.inclOverhead} /> over{' '}
          <Count value={report.layer.birdsAtLayerStart} /> birds at layer start ={' '}
          <Money value={report.layer.costPerLayerBird?.inclOverhead ?? null} /> per layer bird.
        </p>
      </section>

      <EntryGrid
        title="Bird ledger entry"
        description="Mortality, culls, sales and transfers. Enter the count unsigned — the sign follows the event type."
        onSubmit={() =>
          createEvent.mutate(
            {
              batchId: id,
              eventDate,
              eventType,
              qty: Number(qty),
              reason: reason.trim() || null,
              salvageAmount: SALVAGE_TYPES.includes(eventType) && salvage ? salvage : null,
              clientUuid: entryId,
            },
            {
              onSuccess: () => {
                setQty('')
                setSalvage('')
                setReason('')
                setEntryId(clientUuid())
              },
            },
          )
        }
        submitLabel="Record event"
        busy={createEvent.isPending}
        error={createEvent.isError ? createEvent.error.message : null}
      >
        <DateCell label="Date" value={eventDate} onChange={setEventDate} />
        <label className="cell">
          <span className="cell-label">Event</span>
          <select
            className="cell-input"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as BirdEventType)}
          >
            {BIRD_EVENT_TYPE.filter((t) => t !== 'arrival' && t !== 'adjustment').map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <NumericCell label="Birds" value={qty} onChange={setQty} integer />
        {SALVAGE_TYPES.includes(eventType) ? (
          <NumericCell
            label="Salvage proceeds"
            value={salvage}
            onChange={setSalvage}
            hint="Credited against batch cost, never counted as egg revenue"
          />
        ) : null}
        <label className="cell wide">
          <span className="cell-label">Reason</span>
          <input className="cell-input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </EntryGrid>

      <section>
        <h3>Bird ledger</h3>
        <p className="muted">
          Append-only. A correction voids the row and adds a replacement — nothing is edited in place.
        </p>
        <DataTable
          rows={events.data ?? []}
          columns={columns}
          rowKey={(e) => e.id}
          rowClassName={(e) => (e.voidedAt ? 'voided' : undefined)}
          empty={<EmptyState title="No ledger entries beyond the arrival." />}
        />
      </section>
    </div>
  )
}
